import { createContext, forwardRef, useContext, useId } from "react";
import { cn } from "../lib/cn";

interface FieldControlContextValue {
  controlId: string;
  descriptionId?: string;
  invalid: boolean;
  required: boolean;
}

const FieldControlContext = createContext<FieldControlContextValue | null>(
  null,
);

interface FieldControlOptions {
  id?: string;
  describedBy?: string;
  invalid?: React.AriaAttributes["aria-invalid"];
  required?: React.AriaAttributes["aria-required"];
}

/** Applies the nearest Field's label, description and validation state. */
export function useFieldControlProps({
  id,
  describedBy,
  invalid,
  required,
}: FieldControlOptions = {}) {
  const field = useContext(FieldControlContext);
  const descriptions = [describedBy, field?.descriptionId].filter(Boolean);

  return {
    id: id ?? field?.controlId,
    "aria-describedby": descriptions.length
      ? descriptions.join(" ")
      : undefined,
    "aria-invalid": invalid || field?.invalid || undefined,
    "aria-required": required || field?.required || undefined,
  };
}

export interface FieldProps {
  /** Label text; rendered above the control and associated via htmlFor. */
  label?: React.ReactNode;
  htmlFor?: string;
  /** Error message — replaces the hint when both are set. */
  error?: string;
  /** Helper text below the control; hidden when an error is set. */
  hint?: React.ReactNode;
  /** Appends `*` to the label (aria wiring stays with the caller). */
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * Label/error/hint wrapper. Design-system controls inside it inherit the
 * label association, description and validation state automatically.
 */
export const Field = forwardRef<HTMLDivElement, FieldProps>(
  (
    { label, htmlFor, error, hint, required = false, children, className },
    ref,
  ) => {
    const generatedId = useId();
    const controlId = htmlFor ?? generatedId;
    const descriptionId =
      error || hint ? `${controlId}-description` : undefined;

    return (
      <FieldControlContext.Provider
        value={{
          controlId,
          descriptionId,
          invalid: Boolean(error),
          required,
        }}
      >
        <div ref={ref} data-slot="field" className={cn("w-full", className)}>
          {label ? (
            <label
              data-slot="field-label"
              htmlFor={controlId}
              className="mb-1.5 block type-label text-content-secondary"
            >
              {label}
              {required ? <span aria-hidden="true">{" *"}</span> : null}
            </label>
          ) : null}
          {children}
          {error ? (
            <p
              id={descriptionId}
              data-slot="field-description"
              role="alert"
              className="mt-1.5 type-caption text-content-error"
            >
              {error}
            </p>
          ) : hint ? (
            <p
              id={descriptionId}
              data-slot="field-description"
              className="mt-1.5 type-caption text-content-secondary"
            >
              {hint}
            </p>
          ) : null}
        </div>
      </FieldControlContext.Provider>
    );
  },
);
Field.displayName = "Field";
